import { useEffect } from "react";
import { useRouter } from "next/router";

import axios from "axios";
import { NextSeo } from "next-seo";

import { formatImageUrl } from "@/components/blog/Util";
import { useCalendarLink } from "@/components/hooks/useCalendarLink";
import CaseStudies from "@/components/partners/CaseStudies";
import Faqs from "@/components/partners/Faqs";
import FinalCTA from "@/components/partners/FinalCTA";
import Footer from "@/components/partners/Footer";
import Header from "@/components/partners/Header";
import Hero from "@/components/partners/Hero";
import HowItWorks from "@/components/partners/HowItWorks";
import Pricing from "@/components/partners/Pricing";
import Products from "@/components/partners/Products";
import Stats from "@/components/partners/Stats";
import { getPageBySlug } from "@/lib/contentful";

import type { CaseStudy } from "@/pages/case-studies/[slug]";
import type { ContentfulAsset } from "@/types/contentful";

export type HeaderSubheaderComponent = {
  fields: {
    header: string;
    subheader: string;
  };
};

export type CaseStudiesFields = Array<{ fields: CaseStudy }>;

export type PricingCard = {
  fields: {
    header: string;
    subheader: string;
    discountPercentage?: number;
    includes: string[];
    buttonText: string;
    price?: number;
    frequency?: string;
  };
};

export type PartnerProductType = "CRM" | "Marketing" | "Both";

function getPartnerProductType(crmPartner?: boolean, mktPartner?: boolean): PartnerProductType {
  if (crmPartner && !mktPartner) return "CRM";
  if (mktPartner && !crmPartner) return "Marketing";
  return "Both";
}

export type Partner = {
  seoTitle: string;
  seoDescription: string;
  partnerDescription?: string;
  externalPartnerUrl: string;
  partnerName: string;
  industry?: string;
  ownerName?: string;
  bannerText?: string;
  slug: string;
  statCards?: HeaderSubheaderComponent[];
  heroImage?: ContentfulAsset;
  heroVideoUrl?: string;
  heroVideoThumbnail?: ContentfulAsset;
  faqs?: HeaderSubheaderComponent[];
  partnerLogo?: ContentfulAsset;
  createdAt: string;
  pricingCards: PricingCard[];
  finalCTAHeading: string;
  finalCTAButtonText: string;
  caseStudies: CaseStudiesFields;
  calLink: string;
  crmPartner?: boolean;
  mktPartner?: boolean;
};

const Partner = ({ page }: { page: Partner }) => {
  const router = useRouter();
  const { psxId } = useCalendarLink();
  const productType = getPartnerProductType(page.crmPartner, page.mktPartner);
  let ctaLink = page.calLink;
  try {
    const url = new URL(page.calLink);
    if (psxId) url.searchParams.set("ps_xid", psxId);
    ctaLink = url.toString();
  } catch (error) {
    console.error("Error creating calendar link:", error);
  }

  // have to hard code this in for ewing
  useEffect(() => {
    if (!router.isReady || page.slug !== "ewing") {
      return;
    }

    const data = {
      companyName: router.query.companyName,
      memberId: router.query.memberId,
      city: router.query.city,
      state: router.query.state,
      usePoints: router.query.usePoints === "1",
    };

    // Only make the request if we have a memberId
    if (data.memberId) {
      axios.post("/api/external/leads/ewing", data).catch((error) => {
        console.error("Error logging Ewing lead:", error);
      });
    }
  }, [router.isReady, router.query, page.slug]);

  return (
    <div className="bg-ash-100">
      <NextSeo
        title={page?.seoTitle}
        description={page?.seoDescription}
        canonical={`https://rebolthq.com/partners/${page.slug}`}
        openGraph={{
          url: `https://rebolthq.com/partners/${page.slug}`,
          title: page.seoTitle,
          description: page.seoDescription,
          images: page.heroImage
            ? [{ url: formatImageUrl(page.heroImage.fields.file.url) }]
            : [
                {
                  url: `https://api.screenshotone.com/take?access_key=cQWOH7FyQTatDw&url=https://rebolthq.com/partners/${page.slug}&full_page=false&viewport_width=1200&viewport_height=850&device_scale_factor=1&format=jpg&image_quality=80&cache=true&cache_ttl=304800`,
                },
              ],
        }}
      />
      <Header partnerLogo={formatImageUrl(page.partnerLogo?.fields?.file?.url || "")} />
      {page.bannerText && (
        <div className="w-full h-10 bg-black py-2 flex items-center justify-center">
          <p className="text-white font-bold text-center">
            <span className="text-rebolt-500 mr-2">•</span> {page.bannerText}
            <span className="text-rebolt-500 ml-2">•</span>
          </p>
        </div>
      )}
      <Hero
        heroImage={formatImageUrl(page.heroImage?.fields.file.url || "")}
        partnerName={page.partnerName}
        partnerLogo={formatImageUrl(page.partnerLogo?.fields?.file?.url || "")}
        heroVideoUrl={page.heroVideoUrl || ""}
        heroVideoThumbnail={formatImageUrl(page.heroVideoThumbnail?.fields.file.url || "")}
        ctaLink={ctaLink}
      />
      <Pricing pricingCards={page.pricingCards} ctaLink={ctaLink} />
      <Products productType={productType} />
      <Stats statCards={page.statCards} />
      {page.caseStudies && <CaseStudies caseStudies={page.caseStudies} />}
      <HowItWorks ctaLink={ctaLink} />
      <Faqs faqs={page.faqs} />
      <FinalCTA
        finalCTAHeading={page.finalCTAHeading}
        partnerName={page.partnerName}
        finalCTAButtonText={page.finalCTAButtonText}
        ctaLink={ctaLink}
      />
      <Footer />
    </div>
  );
};

export const getServerSideProps = async ({ params }: { params: { partnerName: string } }) => {
  const page = await getPageBySlug(params.partnerName, "pagePartner");

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page,
    },
  };
};

export default Partner;
